// Tolerant language resolution for per-language template maps
// (html / texts / mailSubject, each keyed by a language code).
//
// Mirror of the worker's `main-consumer/src/lib/lang-resolve.ts` — kept in sync
// by hand (no shared package between repos). Background: guest.language and
// template map keys are a mix of short ("de") and full ("de-DE") codes, so an
// exact-match lookup silently falls through to the primary language whenever
// the two granularities disagree. This resolver tries a small candidate chain
// (exact → base → region-collapse) in both directions so "de" and "de-DE"
// interoperate.

/**
 * Expand a language code into the ordered list of keys we try against a map.
 *   "de-DE" → ["de-DE", "de-de", "de"]
 *   "de"    → ["de", "de-DE"]   (region-collapse, so a short-code value matches a full-locale key)
 */
export function langCandidates(code: string | null | undefined): string[] {
  if (typeof code !== "string") return [];
  const c = code.trim();
  if (c.length === 0) return [];

  const out: string[] = [];
  const push = (v: string): void => {
    if (v.length > 0 && !out.includes(v)) out.push(v);
  };

  push(c);
  push(c.toLowerCase());

  const base = c.toLowerCase().split("-")[0]!;
  push(base);
  push(`${base}-${base.toUpperCase()}`);

  return out;
}

/**
 * The first map KEY that speaks the same base language as `code`, or undefined.
 *
 * The region-collapse in {@link langCandidates} only PRETENDS to cover this: it
 * guesses `${base}-${BASE}`, which is a real locale for de/fr/it/es/nl/pt and
 * nonsense everywhere else. Measured 2026-08-21 against the cockpit's own
 * dropdown (84 base languages): 58 collapse to a locale that does not exist.
 * Harmless for the single-variant ones — guest and template both carry it, so
 * the exact match fires. The damage is in multi-variant languages, English
 * worst of all, because `en-EN` is not a locale:
 *
 *     "en-US" against keys {de-DE, en-GB}, primary de-DE
 *       candidates [en-US, en-us, en, en-EN] -> no hit -> GERMAN
 *
 * Same step, deliberately the same wording, as the base-language fallback that
 * `resolveLangKey(available[])` has always had — that takes a LIST of tags,
 * this takes the keys of a map. Keys are sorted so the pick cannot depend on
 * which variant happened to be stored first.
 */
function baseLanguageKey(
  map: Record<string, string>,
  code: string | null | undefined,
): string | undefined {
  const base = baseOf(code);
  if (base.length === 0) return undefined;
  for (const key of Object.keys(map).sort()) {
    if (baseOf(key) !== base) continue;
    const v = map[key];
    if (typeof v === "string" && v.length > 0) return key;
  }
  return undefined;
}

function baseOf(code: string | null | undefined): string {
  if (typeof code !== "string") return "";
  return code.trim().toLowerCase().split("-")[0] ?? "";
}

/**
 * Resolve the best value from a per-language map for the given language,
 * falling back to `primary`, then any non-empty value, then `fallback`.
 * Empty-string entries are treated as missing.
 */
export function resolveLangValue(
  map: Record<string, string> | undefined | null,
  lang: string | null | undefined,
  primary: string | null | undefined,
  fallback: string,
): string {
  if (map === undefined || map === null) return fallback;

  const lookup = (keys: string[]): string | undefined => {
    for (const key of keys) {
      const v = map[key];
      if (typeof v === "string" && v.length > 0) return v;
    }
    return undefined;
  };

  const byLang = lookup(langCandidates(lang));
  if (byLang !== undefined) return byLang;

  // Any variant of the requested language before dropping to a different
  // language entirely — see baseLanguageKey.
  const byBase = baseLanguageKey(map, lang);
  if (byBase !== undefined) return map[byBase]!;

  const byPrimary = lookup(langCandidates(primary));
  if (byPrimary !== undefined) return byPrimary;
  const byPrimaryBase = baseLanguageKey(map, primary);
  if (byPrimaryBase !== undefined) return map[byPrimaryBase]!;

  for (const v of Object.values(map)) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return fallback;
}

/**
 * Pick the best matching language TAG from a list of available tags for the
 * given language, using the same candidate chain. Falls back to `primary`,
 * then the first available tag. Used to choose which language a preview /
 * send should render when a recipient language is known.
 *
 * After the exact candidate chain, falls back to a base-language match so a
 * region variant resolves to a different region of the same language (e.g. a
 * visitor's "en-US" matches an available "en-GB" instead of dropping to the
 * primary language).
 */
export function resolveLangKey(
  available: string[],
  lang: string | null | undefined,
  primary: string,
): string {
  const has = (key: string): boolean => available.includes(key);

  for (const cand of langCandidates(lang)) {
    if (has(cand)) return cand;
  }

  // Base-language fallback: "en-US" → first available "en-*".
  const baseOf = (code: string | null | undefined): string =>
    typeof code === "string" ? (code.toLowerCase().split("-")[0] ?? "") : "";
  const langBase = baseOf(lang);
  if (langBase.length > 0) {
    const byBase = available.find((a) => baseOf(a) === langBase);
    if (byBase !== undefined) return byBase;
  }

  for (const cand of langCandidates(primary)) {
    if (has(cand)) return cand;
  }
  return available.includes(primary) ? primary : (available[0] ?? primary);
}
